import { useState, useEffect } from 'react';
import { type MoodEntry, loadEntries } from './api/mockData';
import { Header } from './components/Header';
import { TodayView } from './components/TodayView';
import { CalendarView } from './components/CalendarView';
import './index.css';

type View = 'today' | 'calendar';

export function App() {
  const [view, setView] = useState<View>('today');
  const [entries, setEntries] = useState<Record<string, MoodEntry>>({});

  useEffect(() => {
    setEntries(loadEntries());
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      minHeight: '100dvh',
      maxWidth: '480px',
      margin: '0 auto',
      background: 'var(--journal-bg)',
      position: 'relative',
      boxShadow: '0 0 60px rgba(58, 46, 40, 0.06)',
    }}>
      <Header
        title={view === 'today' ? 'Mood Journal' : 'Calendar'}
        subtitle={view === 'today' ? 'Track how you feel' : 'Your mood history'}
      />

      {/* Main content */}
      <main style={{ paddingBottom: '80px' }}>
        {view === 'today' ? (
          <TodayView entries={entries} onEntriesChange={setEntries} />
        ) : (
          <CalendarView entries={entries} onEntriesChange={setEntries} />
        )}
      </main>

      {/* Bottom tab bar */}
      <nav style={{
        position: 'fixed',
        bottom: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '100%',
        maxWidth: '480px',
        background: 'rgba(255, 255, 255, 0.92)',
        backdropFilter: 'blur(12px)',
        borderTop: '1px solid var(--journal-line)',
        display: 'flex',
        padding: '8px 24px',
        paddingBottom: 'max(8px, env(safe-area-inset-bottom))',
        zIndex: 50,
      }}>
        <TabButton
          active={view === 'today'}
          onClick={() => setView('today')}
          icon="✏️"
          label="Today"
        />
        <TabButton
          active={view === 'calendar'}
          onClick={() => setView('calendar')}
          icon="📅"
          label="Calendar"
        />
      </nav>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: string;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '2px',
        padding: '6px 0',
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        transition: 'all 0.2s',
      }}
    >
      <span style={{
        fontSize: '1.3rem',
        filter: active ? 'none' : 'grayscale(60%)',
        opacity: active ? 1 : 0.5,
        transition: 'all 0.2s',
        transform: active ? 'scale(1.1)' : 'scale(1)',
      }}>
        {icon}
      </span>
      <span style={{
        fontFamily: 'var(--font-body)',
        fontSize: '0.65rem',
        fontWeight: active ? 700 : 500,
        color: active ? 'var(--journal-accent)' : 'var(--journal-ink-faint)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        transition: 'all 0.2s',
      }}>
        {label}
      </span>
    </button>
  );
}

export default App;
